/**
 * The M8 5(a) mineral post-pass (MC-a2a, D-TBD T19): each system with an asteroid field gets a seeded signature ore
 * (with `secondaryChance` of a second one), and each asteroid field gets a deposit grade and a separate
 * `mineralWeights` mix. `oreWeights` is never touched, so everything that reads it stays byte-identical.
 * Load-time code: allocation is fine here.
 */
import { createRng, nextFloat, type Rng } from '../core/rng'
import { galaxyGradeIds, galaxyOres, type GalaxyConfig, type GalaxyGradeId } from '../data/galaxyConfig'
import { deriveSeed, pickWeighted } from './seed'
import type { Archetype, Field, MineralOre, StarSystem, SystemSignature } from './types'

type MineralsCfg = GalaxyConfig['minerals']
const TETHER = 'tetherstone'
/** The ores the band mix may set: every raw ore but tetherstone, which keeps its `oreWeights` share untouched. */
const isMineralOre = (o: (typeof galaxyOres)[number]): o is MineralOre => o !== TETHER
const BAND_ORES: readonly MineralOre[] = galaxyOres.filter(isMineralOre)
/** The Mk III ore (5(e), MC-e2): never in the M8 mix or signature roll; `applyRefractory` adds it after them. */
const REFRACTORY: MineralOre = 'refractory_ore'
/** The ores the M8 roll mixes (the v6 set): every band ore but the refractory ore. */
const M8_ORES: readonly MineralOre[] = BAND_ORES.filter((o) => o !== REFRACTORY)

/** True when a system of `archetype` never carries `ore` (`minerals.archetypeExclude`; 5(e): no refractory ore in a vein). */
export function excluded(m: MineralsCfg, archetype: Archetype | undefined, ore: MineralOre): boolean {
  return archetype !== undefined && (m.archetypeExclude[archetype]?.includes(ore) ?? false)
}

/** Star-class bias for one ore (D-TBD T19); an absent class or ore counts 1. */
function starBiasOf(m: MineralsCfg, starClass: StarSystem['star']['class'], ore: MineralOre): number {
  return m.signature.starBias[starClass]?.[ore] ?? 1
}

/** Picks a signature ore from `candidates`, weighted by the star-class bias. */
function pickOre(rng: Rng, m: MineralsCfg, starClass: StarSystem['star']['class'], candidates: readonly MineralOre[]): MineralOre {
  const weights: Partial<Record<MineralOre, number>> = {}
  for (const ore of candidates) weights[ore] = starBiasOf(m, starClass, ore)
  return pickWeighted(rng, weights as Record<MineralOre, number>)
}

/**
 * Clamps each ore's share to at most `cap` × its band share (T18) and hands the excess to the unclamped ores in
 * proportion, repeating until nothing is over. `shares` and `band` are both normalised; `cap` ≥ 1 keeps it feasible.
 */
export function clampShares(shares: readonly number[], band: readonly number[], cap: number): number[] {
  const out = [...shares]
  const fixed = out.map(() => false)
  for (let pass = 0; pass < out.length; pass++) {
    let over = false
    for (let i = 0; i < out.length; i++) {
      if (!fixed[i] && out[i] > cap * band[i]) {
        out[i] = cap * band[i]
        fixed[i] = true
        over = true
      }
    }
    if (!over) break
    const fixedSum = out.reduce((s, v, i) => (fixed[i] ? s + v : s), 0)
    const freeSum = out.reduce((s, v, i) => (fixed[i] ? s : s + v), 0)
    if (!(freeSum > 0)) break
    const k = (1 - fixedSum) / freeSum
    for (let i = 0; i < out.length; i++) if (!fixed[i]) out[i] *= k
  }
  return out
}

/**
 * The asteroid mix of a system: normalise(band[region] × signature × starBias), clamped by `stackCap` relative to the
 * band, then fitted inside `1 − w(tetherstone)` with the field's tetherstone weight copied unchanged. This is the v6 mix
 * over the M8 ores only: the refractory ore's share is added afterwards by `withRefractory` (5(e), MC-e2).
 */
export function mineralMix(
  m: MineralsCfg,
  region: StarSystem['region'],
  starClass: StarSystem['star']['class'],
  signature: SystemSignature,
  oreWeights: Readonly<Record<string, number>>,
): Record<string, number> {
  const bandRaw = M8_ORES.map((o) => m.regionBands[region][o] ?? 0)
  const bandTotal = bandRaw.reduce((s, w) => s + w, 0)
  const band = bandRaw.map((w) => w / bandTotal)
  const raw = M8_ORES.map((o, i) => {
    const sig = o === signature.primary ? m.signature.mult : o === signature.secondary ? m.signature.secondaryMult : 1
    return band[i] * sig * starBiasOf(m, starClass, o)
  })
  const rawTotal = raw.reduce((s, w) => s + w, 0)
  const shares = clampShares(
    raw.map((w) => w / rawTotal),
    band,
    m.stackCap,
  )
  const tether = oreWeights[TETHER]
  const room = 1 - (tether ?? 0)
  const out: Record<string, number> = {}
  M8_ORES.forEach((o, i) => {
    if (shares[i] > 0) out[o] = shares[i] * room
  })
  if (tether !== undefined) out[TETHER] = tether
  return out
}

function rollSignature(rng: Rng, m: MineralsCfg, sys: StarSystem): SystemSignature {
  // The schema already refuses tetherstone here; the filter only narrows the type.
  const eligible = m.signature.eligible[sys.region].filter(isMineralOre)
  const primary = pickOre(rng, m, sys.star.class, eligible)
  const rest = eligible.filter((o) => o !== primary)
  const second = nextFloat(rng) < m.signature.secondaryChance
  return second && rest.length > 0 ? { primary, secondary: pickOre(rng, m, sys.star.class, rest) } : { primary }
}

function rollGrade(rng: Rng, m: MineralsCfg, region: StarSystem['region']): GalaxyGradeId {
  const weights: Partial<Record<GalaxyGradeId, number>> = {}
  m.grades.weights[region].forEach((w, i) => {
    weights[galaxyGradeIds[i]] = w
  })
  return pickWeighted(rng, weights as Record<GalaxyGradeId, number>)
}

/** One system's post-pass on `deriveSeed(galaxySeed, 'minerals', index)`; systems without an asteroid field pass through. */
export function applySystemMinerals(galaxySeed: number, sys: StarSystem, m: MineralsCfg): StarSystem {
  if (!sys.fields.some((f) => f.kind === 'asteroid')) return sys
  const rng = createRng(deriveSeed(galaxySeed, 'minerals', sys.index))
  const signature = rollSignature(rng, m, sys)
  return {
    ...sys,
    signature,
    fields: sys.fields.map((f) => withFieldMinerals(rng, m, sys, signature, f)),
  }
}

function withFieldMinerals(rng: Rng, m: MineralsCfg, sys: StarSystem, signature: SystemSignature, f: Field): Field {
  if (f.kind !== 'asteroid') return f
  const grade = rollGrade(rng, m, sys.region)
  return {
    ...f,
    mineralWeights: mineralMix(m, sys.region, sys.star.class, signature, f.oreWeights),
    grade,
  }
}

/** The post-pass over every system (MC-a2a). Pure and deterministic; never edits `oreWeights` or anything else. */
export function applyMinerals(galaxySeed: number, systems: readonly StarSystem[], cfg: GalaxyConfig): StarSystem[] {
  return systems.map((sys) => applySystemMinerals(galaxySeed, sys, cfg.minerals))
}

/**
 * The refractory ore's share of an asteroid field in `region` around a `starClass` star (5(e), MC-e2): its band share
 * (normalised over the whole region band) × `signature.mult` when it is the system's refractory signature × its star
 * bias, clamped to `stackCap` × the band share. 0 outside the deep and rim.
 */
export function refractoryShare(m: MineralsCfg, region: StarSystem['region'], starClass: StarSystem['star']['class'], signature: boolean): number {
  const band = m.regionBands[region]
  const b = band[REFRACTORY] ?? 0
  if (!(b > 0)) return 0
  const total = BAND_ORES.reduce((sum, o) => sum + (band[o] ?? 0), 0)
  const mult = Math.min((signature ? m.signature.mult : 1) * starBiasOf(m, starClass, REFRACTORY), m.stackCap)
  return (b / total) * mult
}

/** A field's mix with the refractory ore at `share` of the non-tetherstone room; every other ore only scales by 1 − share. */
export function withRefractory(weights: Readonly<Record<string, number>>, share: number): Record<string, number> {
  const tether = weights[TETHER]
  const out: Record<string, number> = {}
  for (const [ore, w] of Object.entries(weights)) out[ore] = ore === TETHER ? w : w * (1 - share)
  out[REFRACTORY] = share * (1 - (tether ?? 0))
  return out
}

/**
 * One system's refractory pass on its own `deriveSeed(galaxySeed, 'minerals-refractory', index)` stream, run after the
 * M8 rolls and the mineral guarantee, so the v6 signature and grades never move. A deep or rim system with an asteroid
 * field (not of an archetype that excludes the ore) rolls whether it is a refractory signature, and every asteroid
 * field gains the ore's share. Everything else passes through unchanged. `force` (the MC-e6b guarantee) makes an
 * eligible system a refractory signature whatever its roll.
 */
export function applySystemRefractory(galaxySeed: number, sys: StarSystem, m: MineralsCfg, force = false): StarSystem {
  if (sys.signature === undefined || excluded(m, sys.archetype, REFRACTORY) || !((m.regionBands[sys.region][REFRACTORY] ?? 0) > 0)) return sys
  const rng = createRng(deriveSeed(galaxySeed, 'minerals-refractory', sys.index))
  const chance = Math.min(1, (m.refractory.signatureChance[sys.region] ?? 0) * starBiasOf(m, sys.star.class, REFRACTORY))
  const isSignature = nextFloat(rng) < chance || force
  const share = refractoryShare(m, sys.region, sys.star.class, isSignature)
  return {
    ...sys,
    signature: isSignature ? { ...sys.signature, refractory: true } : sys.signature,
    fields: sys.fields.map((f) =>
      f.kind === 'asteroid' && f.mineralWeights !== undefined ? { ...f, mineralWeights: withRefractory(f.mineralWeights, share) } : f,
    ),
  }
}

/** The refractory post-pass over every system (5(e), MC-e2); runs after `applyMinerals` and the mineral guarantee. */
export function applyRefractory(galaxySeed: number, systems: readonly StarSystem[], cfg: GalaxyConfig): StarSystem[] {
  return systems.map((sys) => applySystemRefractory(galaxySeed, sys, cfg.minerals))
}
